import { setFieldValue } from 'actions/ui/ui';
import { SET_DEFAULT_FIELD_VALUES } from 'constants/fields';
import {
  FETCH_SENDER_FAILURE,
  FETCH_SENDER_REQUEST,
  FETCH_SENDER_SUCCESS,
  UPDATE_SENDER_EMAIL,
} from 'constants/sender';
import { getPayment } from 'selectors/entities';
import { getRecipientId } from 'selectors/entities/recipient/recipient';
import {
  getCurrentCountryCode,
  getCurrentLocale,
  getFieldValue,
  getSelectedOffer,
  isFetching,
} from 'selectors/ui/ui';
import type { FetchSendersParams } from 'services/senders/senders';
import { senders } from 'services/senders/senders';
import type { PayexThunkAction } from 'store/configureStore';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import type { I18n } from 'utils/translations/translations';

type SenderFieldsPayload = {
  entities?: {
    fields?: {
      sender_type?: {
        required?: boolean;
        value?: Array<{ value?: string }> | string;
      };
    };
  };
};

const getDefaultSenderType = (
  payload: SenderFieldsPayload,
  currentType: string,
): string | null => {
  const senderTypeField = payload.entities?.fields?.sender_type;
  const shouldSetDefaultType =
    senderTypeField?.required === true && !currentType;

  if (!shouldSetDefaultType) {
    return null;
  }

  const fieldValue = senderTypeField.value;
  return Array.isArray(fieldValue) && fieldValue[0]?.value
    ? (fieldValue[0].value as string)
    : null;
};

export const fetchSender =
  (i18n?: I18n): PayexThunkAction<Promise<void>> =>
  (dispatch, getState) => {
    const state = getState();
    const isFetchingSender = isFetching(state, 'sender');

    if (isFetchingSender) {
      return Promise.resolve();
    }

    dispatch({
      type: FETCH_SENDER_REQUEST,
    });

    const payment = getPayment(state);
    const country = getCurrentCountryCode(state) as string;
    const locale = getCurrentLocale(state);
    const type = getFieldValue(state, 'sender_type') as string;
    const recipientCode = getRecipientId(state);

    const params: FetchSendersParams = {
      country,
      locale,
      type: type !== '' ? type : undefined,
      recipientCode: recipientCode ?? undefined,
    };

    if (payment && payment.id) {
      params.paymentMethod = payment.offer.paymentMethodInternalName;
      params.paymentMethodType = payment.offer.type;
      params.paymentOption = payment.offer.paymentOption;
      params.priceAmount = payment.price.value;
      params.priceCurrency = payment.price.currency;
      params.purchaseCurrency = payment.purchase.currency;
    } else {
      const selectedOffer = getSelectedOffer(state);
      params.paymentMethod = selectedOffer.paymentMethodName;
      params.paymentMethodType = selectedOffer.paymentMethod;
      params.paymentOption = selectedOffer.paymentOption;
      params.priceAmount = selectedOffer.price?.value;
      params.priceCurrency = selectedOffer.price?.currency;
      params.purchaseCurrency = selectedOffer.purchase?.currency.code;
    }

    return senders
      .fetch({ ...params, i18n })
      .then(payload => {
        const defaultSenderType = getDefaultSenderType(payload, type);
        if (defaultSenderType) {
          dispatch(setFieldValue('sender_type', defaultSenderType));
        }
        dispatch({
          type: FETCH_SENDER_SUCCESS,
          payload,
        });

        dispatch({
          type: SET_DEFAULT_FIELD_VALUES,
          payload: payload,
        });
      })
      .catch(error => {
        errorNotifier.notify(error);

        dispatch({ type: FETCH_SENDER_FAILURE });
      });
  };

export const updateSenderEmail =
  (email: string): PayexThunkAction =>
  dispatch =>
    dispatch({ type: UPDATE_SENDER_EMAIL, payload: email });
